/**
 * Where the link in the sign-up email lands.
 *
 * The key is submitted as soon as the page opens, because the user has already acted by clicking
 * the link; asking them to press another button would be a second step for nothing.
 *
 * allauth answers in three ways, and telling them apart is the whole job here:
 *
 *   2xx                           verified, and signed in — go to the app.
 *   401 with no field errors      verified, but no session. This is what happens when the link is
 *                                 opened in a different browser from the one that signed up,
 *                                 because the pending login lives in that first session's cookie.
 *                                 Signing up on a phone and reading email on a laptop does it, so
 *                                 it is ordinary rather than exotic. Tell them to sign in.
 *   400 invalid_or_expired_key    the key is wrong, expired, or already used. A real failure.
 *
 * Treating the middle case as a failure tells someone their link is broken moments after it
 * worked, and sends them back to click it again — which then genuinely fails, because the key is
 * single-use.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { auth, AuthError } from "@/api/auth";
import { Notice } from "@/components/ui/Field";
import { AuthCard } from "@/features/auth/AuthLayout";
import { sessionChanged } from "@/features/auth/session";

type State =
  { status: "working" } | { status: "confirmed" } | { status: "failed"; message: string };

/** Verified, but allauth granted no session: a 401 that carries no complaint about the key. */
export function isConfirmedWithoutSession(error: unknown): boolean {
  return error instanceof AuthError && error.status === 401 && error.errors.length === 0;
}

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const { key } = useParams({ from: "/auth/verify-email/$key" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>({ status: "working" });
  // The key is single-use, so submitting it twice would fail the second time. React in
  // development mounts effects twice, which would do exactly that.
  const submitted = useRef(false);

  useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;

    void (async () => {
      try {
        await auth.verifyEmail(key);
        await sessionChanged(queryClient);
        await navigate({ to: "/" });
      } catch (error) {
        if (isConfirmedWithoutSession(error)) {
          setState({ status: "confirmed" });
          return;
        }
        const message =
          error instanceof AuthError && error.errors.length > 0
            ? error.errors[0]!.message
            : t("auth.verify.failedBody");
        setState({ status: "failed", message });
      }
    })();
  }, [key, navigate, queryClient, t]);

  if (state.status === "working") {
    return (
      <AuthCard title={t("auth.verify.workingTitle")}>
        <Notice tone="info">{t("auth.verify.workingBody")}</Notice>
      </AuthCard>
    );
  }

  if (state.status === "confirmed") {
    return (
      <AuthCard
        title={t("auth.verify.confirmedTitle")}
        description={t("auth.verify.confirmedBody")}
      >
        <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
          {t("auth.signIn.submit")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("auth.verify.failedTitle")}>
      <Notice tone="error">{state.message}</Notice>
      <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
        {t("auth.backToSignIn")}
      </Link>
    </AuthCard>
  );
}
